import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import EquityLogo from "@/components/EquityLogo";
import { useBranding } from "@/hooks/useBranding";

const SIDEBAR_KEY = "equity_sidebar_collapsed";

export function getStoredSidebarCollapsed() {
  try { return localStorage.getItem(SIDEBAR_KEY) === "1"; } catch { return false; }
}

export default function Sidebar({ collapsed, onToggle, groups, isActive, onNavigate }) {
  const navigate = useNavigate();
  const { companyName } = useBranding();

  return (
    <aside
      className={`sidebar-dark hidden md:flex md:flex-col shrink-0 sticky top-0 h-screen z-30 border-r border-white/10 transition-[width] duration-200 ease-in-out ${collapsed ? "w-[76px]" : "w-64"}`}
      style={{ backgroundImage: "linear-gradient(185deg, #0f1522 0%, #0b0e16 55%, #060709 100%)" }}
    >
      <div className="relative">
        <button onClick={() => navigate("/dashboard")} className="w-full h-16 flex items-center gap-2.5 px-4 border-b border-white/10 min-w-0 shrink-0">
          <div className="w-9 h-9 rounded-xl bg-[#020617] overflow-hidden flex items-center justify-center shrink-0"><EquityLogo size={32} /></div>
          {!collapsed && (
            <span className="flex flex-col items-start leading-none min-w-0 text-left">
              <span className="font-heading text-xs font-medium tracking-[0.14em] uppercase text-zinc-200 truncate">Equity<span className="text-blue-400">.CRM</span></span>
              <span className="text-[10px] text-zinc-500 font-normal mt-1 truncate max-w-[140px]">{companyName}</span>
            </span>
          )}
        </button>
        <button
          data-testid="sidebar-toggle"
          onClick={onToggle}
          title={collapsed ? "Buka sidebar" : "Tutup sidebar"}
          className="absolute -right-3 top-11 w-6 h-6 rounded-full bg-[#18181B] border border-white/20 shadow-[0_1px_4px_rgba(0,0,0,0.5)] flex items-center justify-center text-zinc-300 hover:text-white hover:border-blue-500/50 transition-colors"
        >
          {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden hide-scrollbar py-4 px-3 space-y-5">
        {groups.map((group) => (
          <div key={group.label}>
            {!collapsed && <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{group.label}</div>}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.to);
                return (
                  <button
                    key={item.to}
                    data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => onNavigate(item.to)}
                    title={collapsed ? item.label : undefined}
                    className={`w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-colors ${collapsed ? "justify-center px-0 h-10" : "px-3 h-10"} ${active ? "text-blue-200" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
                    style={active ? { backgroundImage: "linear-gradient(90deg, rgba(59,130,246,0.22), rgba(59,130,246,0.06))", boxShadow: "inset 1px 0 0 rgba(96,165,250,0.4)" } : undefined}
                  >
                    <Icon size={18} className="shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}
